import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
	useAcceptDuelMutation,
	useDeclineDuelMutation,
	useDuel,
} from "../../lib/api/fantasy-fams";
import { getCurrentUser, type CurrentUser } from "../../lib/auth";
import { SettleCountdown, formatPctSigned, pctColorClass } from "../app/fantasy/_shared";
import type { Duel } from "../../lib/types";

// /duels/:id — PUBLIC spectator page. Anyone (signed-out included) can land
// here from a tweet, see the head-to-head, and tweet trash-talk. The
// accept/decline buttons appear only when the viewer is the opponent's PM.
export function DuelDetailRoute() {
	const { id } = useParams<{ id: string }>();
	const duelQuery = useDuel(id);
	const [user, setUser] = useState<CurrentUser | null>(null);

	useEffect(() => {
		// Public route — getCurrentUser returns null when signed-out. We still
		// call it because the accept/decline gating depends on user.id matching
		// the opponent PM.
		void getCurrentUser().then(setUser);
	}, []);

	if (duelQuery.isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center text-[var(--color-muted)]">
				Loading duel…
			</div>
		);
	}

	if (duelQuery.isError || !duelQuery.data) {
		return (
			<div className="mx-auto max-w-2xl px-4 py-8 text-center">
				<p className="text-sm text-[var(--color-danger)]">
					Could not load duel.{" "}
					{duelQuery.error instanceof Error ? duelQuery.error.message : ""}
				</p>
				<Link
					to="/fantasy/duels"
					className="mt-4 inline-block rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-accent)]"
				>
					Back to duels
				</Link>
			</div>
		);
	}

	const duel = duelQuery.data.duel;
	const isOpponentPM =
		!!user && duel.opponent.pmUserId === user.id && duel.status === "forming";

	return (
		<div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
			<header className="space-y-1">
				<Link
					to="/fantasy/duels"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Duels
				</Link>
				<h1 className="text-2xl font-bold">Duel</h1>
				<p className="text-xs uppercase tracking-wider text-[var(--color-muted)]">
					{duel.cadence} · {duel.status}
				</p>
			</header>

			<HeadToHead duel={duel} />

			{(duel.endsAt ?? duel.startedAt) && duel.status === "active" && (
				<section className="flex justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
					{duel.endsAt && (
						<SettleCountdown target={duel.endsAt} label="Beat them by" />
					)}
				</section>
			)}

			{duel.wager && (
				<section className="rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3 text-center">
					<p className="text-[10px] uppercase tracking-wider text-amber-300">
						Wager
					</p>
					<p className="text-sm font-medium">{duel.wager}</p>
				</section>
			)}

			{isOpponentPM && <OpponentActions duelId={duel.id} />}

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Simulated paper trade. Not investment advice. See{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					disclosures
				</a>
				.
			</footer>
		</div>
	);
}

// --- Head-to-head -----------------------------------------------------------

function HeadToHead({ duel }: { duel: Duel }) {
	const winner = duel.winnerFamId;
	return (
		<section className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto_1fr]">
			<DuelSidePanel
				famName={duel.challenger.famName}
				famSlug={duel.challenger.famSlug}
				returnPct={duel.challenger.returnPct}
				winner={winner === duel.challenger.famId}
				kind="Challenger"
			/>
			<div className="flex items-center justify-center text-3xl text-[var(--color-muted)]">
				⚔
			</div>
			<DuelSidePanel
				famName={duel.opponent.famName}
				famSlug={duel.opponent.famSlug}
				returnPct={duel.opponent.returnPct}
				winner={winner === duel.opponent.famId}
				kind="Opponent"
			/>
		</section>
	);
}

function DuelSidePanel({
	famName,
	famSlug,
	returnPct,
	winner,
	kind,
}: {
	famName: string;
	famSlug: string;
	returnPct?: number;
	winner: boolean;
	kind: "Challenger" | "Opponent";
}) {
	return (
		<Link
			to={`/fam/${famSlug}`}
			className={`flex flex-col items-center gap-2 rounded-xl border bg-[var(--color-bg-card)] p-5 text-center transition hover:border-[var(--color-accent)] ${
				winner
					? "border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/40"
					: "border-[var(--color-border)]"
			}`}
		>
			<p className="text-[10px] uppercase tracking-wider text-[var(--color-muted)]">
				{kind}
			</p>
			<p className="text-xl font-bold">
				{winner && <span aria-hidden>👑 </span>}
				{famName}
			</p>
			<p className="font-mono text-xs text-[var(--color-muted)]">{famSlug}</p>
			<p
				className={`font-mono text-4xl font-bold tabular-nums ${pctColorClass(
					returnPct,
				)}`}
			>
				{formatPctSigned(returnPct)}
			</p>
		</Link>
	);
}

// --- Opponent PM action panel -----------------------------------------------

function OpponentActions({ duelId }: { duelId: string }) {
	const accept = useAcceptDuelMutation();
	const decline = useDeclineDuelMutation();

	return (
		<section className="rounded-lg border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)] p-4">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div>
					<p className="text-sm font-semibold">You've been challenged</p>
					<p className="text-xs text-[var(--color-muted)]">
						Accept to lock both Fams' composites and start the duel; decline
						to pass.
					</p>
				</div>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={() => void decline.mutateAsync({ duelId })}
						disabled={decline.isPending || accept.isPending}
						className="rounded-md border border-[var(--color-border)] px-3 py-1.5 text-sm hover:border-[var(--color-danger)] hover:text-[var(--color-danger)] disabled:opacity-50"
					>
						{decline.isPending ? "Declining…" : "Decline"}
					</button>
					<button
						type="button"
						onClick={() => void accept.mutateAsync({ duelId })}
						disabled={decline.isPending || accept.isPending}
						className="rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-sm font-semibold text-black hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{accept.isPending ? "Accepting…" : "Accept duel"}
					</button>
				</div>
			</div>
			{(accept.error ?? decline.error) && (
				<p className="mt-2 text-xs text-[var(--color-danger)]">
					{(accept.error ?? decline.error)?.code}:{" "}
					{(accept.error ?? decline.error)?.message}
				</p>
			)}
		</section>
	);
}
